/**
 * TaskAssigneeAvatar Component
 *
 * Displays avatar for task assignee
 *
 * @module components/tasks/shared/TaskAssigneeAvatar
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * TaskAssigneeAvatar Component
 *
 * @param {Object} props
 * @param {Object} props.assignee - Assignee object with full_name and avatar_url
 * @param {string} props.size - Size: 'sm', 'md', 'lg', 'xl'
 * @param {string} props.className - Additional CSS classes
 */
const TaskAssigneeAvatar = ({ assignee, size = 'md', className = '' }) => {
  const getInitials = (name) => {
    if (!name) return '?';

    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  const sizeClass = `hex-avatar hex-avatar-${size}`;

  const colors = [
    '#0d6efd',
    '#0dcaf0',
    '#198754',
    '#ffc107',
    '#dc3545'
  ];

  if (!assignee) {
    return (
      <div
        className={`${sizeClass} ${className}`}
        style={{ background: '#6c757d' }}
        title="Sin asignar"
      >
        <i className="bi bi-person-x"></i>
      </div>
    );
  }

  // Simple hash to get consistent color per user
  const colorIndex = (assignee.id || assignee.full_name).split('').reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0
  ) % colors.length;

  if (assignee.avatar_url) {
    return (
      <div
        className={`${sizeClass} ${className}`}
        title={assignee.full_name}
      >
        <img
          src={assignee.avatar_url}
          alt={assignee.full_name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  const initials = getInitials(assignee.full_name);

  return (
    <div
      className={`${sizeClass} ${className}`}
      style={{ background: colors[colorIndex] }}
      title={assignee.full_name}
    >
      {initials}
    </div>
  );
};

TaskAssigneeAvatar.propTypes = {
  assignee: PropTypes.shape({
    id: PropTypes.string,
    full_name: PropTypes.string,
    avatar_url: PropTypes.string
  }),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  className: PropTypes.string
};

export default TaskAssigneeAvatar;
